import React, { useState } from "react";
import { useQuery } from "@apollo/client/react";
import gql from "graphql-tag";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretUp,
  faCaretDown,
  faCheck,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useDates } from "../hooks/useDates";

const FETCH_LIVE_MATCHES = gql`
  query {
    getLiveMatches {
      _id
      Gameweek
      KickoffTime
      HomeTeamId
      HomeTeamName
      AwayTeamId
      AwayTeamName
      HomeScore
      AwayScore
      Started
      Finished
      PlayerStats {
        PlayerId
        PlayerName
        Home
        Minutes
        FplPoints
        GoalsScored
        Assists
        DefensiveContributions
        BonusPoints
        Bps
        YellowCards
        RedCards
        OwnGoals
        PenaltiesSaved
        PenaltiesMissed
        Saves
        FantasyTeamName
        Selected
      }
    }
  }
`;

const STAT_SECTIONS = [
  { key: "GoalsScored", title: "Goals" },
  { key: "Assists", title: "Assists" },
  { key: "OwnGoals", title: "Own Goals" },
  { key: "YellowCards", title: "Yellow Cards" },
  { key: "RedCards", title: "Red Cards" },
  { key: "Saves", title: "Saves" },
  { key: "BonusPoints", title: "Bonus Points" },
  { key: "DefensiveContributions", title: "Defensive Contributions" },
];

function LiveMatches() {
  const { loading, error, data } = useQuery(FETCH_LIVE_MATCHES);
  const [expandedMatches, setExpandedMatches] = useState(new Set());
  const { formatFriendlyDate, formatFriendlyTime } = useDates();

  const getTeamBadge = (teamName) => {
    if (!teamName) {
      return null;
    }

    try {
      return require(`../images/team_badges/${teamName}.svg`);
    } catch (e) {
      return null;
    }
  };

  const toggleMatch = (matchId) => {
    const newExpanded = new Set(expandedMatches);
    if (newExpanded.has(matchId)) {
      newExpanded.delete(matchId);
    } else {
      newExpanded.add(matchId);
    }
    setExpandedMatches(newExpanded);
  };

  const groupMatchesByDate = (matches) => {
    const groups = [];

    (matches || []).forEach((match) => {
      const label = formatFriendlyDate(match.KickoffTime);
      const lastGroup = groups[groups.length - 1];

      if (lastGroup && lastGroup.label === label) {
        lastGroup.matches.push(match);
      } else {
        groups.push({ label, matches: [match] });
      }
    });

    return groups;
  };

  const renderSectionPlayers = (playerStats, sectionKey, isHome) =>
    (playerStats || [])
      .filter((p) => p.Home === isHome && p[sectionKey] > 0)
      .sort((a, b) => b[sectionKey] - a[sectionKey]);

  const getFantasyTeamInitials = (fantasyTeamName) => {
    if (!fantasyTeamName) {
      return "";
    }

    return fantasyTeamName
      .split(" ")
      .filter(Boolean)
      .map((word) => word[0].toUpperCase())
      .join("");
  };

  const renderStatList = (players, sectionKey) => (
    <ul>
      {players.map((p) => (
        <li key={p.PlayerId} className="live-match-stat-player-row">
          <span className="live-match-stat-player-name">
            {p.PlayerName}
            {p[sectionKey] > 1 ? ` (${p[sectionKey]})` : ""}
          </span>
          <span className="live-match-stat-player-meta">
            <span className="live-match-stat-fantasy-team">
              {getFantasyTeamInitials(p.FantasyTeamName)}
            </span>
            {p.FantasyTeamName ? (
              <FontAwesomeIcon
                icon={p.Selected ? faCheck : faXmark}
                className={`live-match-stat-selected-icon ${
                  p.Selected
                    ? "live-match-stat-selected-yes"
                    : "live-match-stat-selected-no"
                }`}
              />
            ) : null}
          </span>
        </li>
      ))}
    </ul>
  );

  if (loading) {
    return <p>Loading live matches...</p>;
  }

  if (error || !data || !data.getLiveMatches) {
    return <p>Could not load live matches.</p>;
  }

  return (
    <div className="live-matches">
      {groupMatchesByDate(data.getLiveMatches).map((group) => (
        <div className="live-match-date-group" key={group.label}>
          <h3 className="live-match-date-heading">{group.label}</h3>
          {group.matches.map((match) => {
            const isExpanded = expandedMatches.has(match._id);

            return (
              <div className="live-match" key={match._id}>
                <div
                  className="live-match-row"
                  onClick={() => toggleMatch(match._id)}
                >
                  <div className="live-match-team live-match-team-home">
                    <span className="live-match-team-name">
                      {match.HomeTeamName}
                    </span>
                    {getTeamBadge(match.HomeTeamName) ? (
                      <img
                        src={getTeamBadge(match.HomeTeamName)}
                        alt={match.HomeTeamName}
                        className="live-match-team-badge"
                      />
                    ) : null}
                  </div>
                  <div className="live-match-score">
                    {match.Started
                      ? `${match.HomeScore} - ${match.AwayScore}`
                      : formatFriendlyTime(match.KickoffTime)}
                  </div>
                  <div className="live-match-team live-match-team-away">
                    {getTeamBadge(match.AwayTeamName) ? (
                      <img
                        src={getTeamBadge(match.AwayTeamName)}
                        alt={match.AwayTeamName}
                        className="live-match-team-badge"
                      />
                    ) : null}
                    <span className="live-match-team-name">
                      {match.AwayTeamName}
                    </span>
                  </div>
                  <FontAwesomeIcon
                    icon={isExpanded ? faCaretUp : faCaretDown}
                    className="live-match-caret"
                  />
                </div>
                {isExpanded && (
                  <div className="live-match-details">
                    {STAT_SECTIONS.map((section) => {
                      const homePlayers = renderSectionPlayers(
                        match.PlayerStats,
                        section.key,
                        true
                      );
                      const awayPlayers = renderSectionPlayers(
                        match.PlayerStats,
                        section.key,
                        false
                      );

                      if (
                        homePlayers.length === 0 &&
                        awayPlayers.length === 0
                      ) {
                        return null;
                      }

                      return (
                        <div
                          className="live-match-stat-section"
                          key={section.key}
                        >
                          <h4 className="live-match-stat-section-title">
                            {section.title}
                          </h4>
                          <div className="live-match-stat-section-row">
                            <div className="live-match-side live-match-side-home">
                              {renderStatList(homePlayers, section.key)}
                            </div>
                            <div className="live-match-side live-match-side-away">
                              {renderStatList(awayPlayers, section.key)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default LiveMatches;

